import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { isValidEmail, normalizePhoneToE164 } from "@athena/common/validation";
import {
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Field,
    FieldError,
    FieldLabel,
    Input,
} from "@athena/ui";
import { formatPhoneInput } from "@/lib/phone";
import type { ClientAccount } from "../helpers/client";

/**
 * A phone as it is typed: a US number as XXX-XXX-XXXX, and one starting with + (another country)
 * as + and its digits. Anything else, letters included, is dropped.
 */
const typedPhone = (raw: string): string =>
    raw.trimStart().startsWith("+") ? `+${raw.replace(/\D/g, "")}` : formatPhoneInput(raw);

/** A stored E.164 phone the way it is typed: a US one without its +1. */
const phoneForInput = (phone: string): string => (phone.startsWith("+1") ? formatPhoneInput(phone.slice(2)) : phone);

/** What is saved. Null removes one, so the sign-in email or phone is used again. */
export interface ContactDetails {
    contactEmail: string | null;
    contactPhone: string | null;
}

/** One of the two contact details on an account. */
export type ContactField = keyof ContactDetails;

interface ContactDetailsDialogProps {
    /** The fields being edited, or none while closed. */
    fields: ContactField[];
    onClose: () => void;
    firstName: string;
    account: ClientAccount;
    /**
     * Saves what was entered for `fields`, the rest left as they are. The dialog closes once it has;
     * a failed save keeps it open with what was entered.
     */
    onSave: (entered: Partial<ContactDetails>) => Promise<void>;
}

export function ContactDetailsDialog({ fields, onClose, firstName, account, onSave }: ContactDetailsDialogProps) {
    const { t } = useTranslation();
    // One field is the edit button on its row; more is "Add phone or email" offering what's missing.
    const title =
        fields.length === 1
            ? t(
                  fields[0] === "contactEmail"
                      ? "admin.alumni.profile.contactDetails.editEmail"
                      : "admin.alumni.profile.contactDetails.editPhone",
              )
            : t("admin.alumni.profile.contactDetails.add");
    return (
        <Dialog open={fields.length > 0} onOpenChange={(open) => (open ? undefined : onClose())}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    <DialogDescription>
                        {t("admin.alumni.profile.contactDetails.description", {
                            firstName,
                            email: account.signInEmail,
                        })}
                    </DialogDescription>
                </DialogHeader>
                {/* Its own component so its state resets each time the dialog opens — Radix unmounts the content on close. */}
                <ContactDetailsForm
                    fields={fields}
                    account={account}
                    onCancel={onClose}
                    onSubmit={async (entered) => {
                        await onSave(entered);
                        onClose();
                    }}
                />
            </DialogContent>
        </Dialog>
    );
}

function ContactDetailsForm({
    fields,
    account,
    onCancel,
    onSubmit,
}: {
    fields: ContactField[];
    account: ClientAccount;
    onCancel: () => void;
    onSubmit: (entered: Partial<ContactDetails>) => Promise<void>;
}) {
    const { t } = useTranslation();
    const ids = useId();
    const [
        contactEmail,
        setContactEmail,
    ] = useState<string>(account.contactEmail ?? "");
    const [
        contactPhone,
        setContactPhone,
    ] = useState<string>(account.contactPhone ? phoneForInput(account.contactPhone) : "");
    const [
        saving,
        setSaving,
    ] = useState<boolean>(false);
    // Errors show once Save has been tried, and then follow what is typed.
    const [
        triedSaving,
        setTriedSaving,
    ] = useState<boolean>(false);

    // Blank removes it, so only a value that is there can be wrong. The same checks the API makes.
    const emailInvalid = fields.includes("contactEmail") && contactEmail.trim() !== "" && !isValidEmail(contactEmail);
    const phoneInvalid =
        fields.includes("contactPhone") && contactPhone.trim() !== "" && !normalizePhoneToE164(contactPhone);
    const showEmailError = triedSaving && emailInvalid;
    const showPhoneError = triedSaving && phoneInvalid;

    const submit = async () => {
        setTriedSaving(true);
        if (emailInvalid || phoneInvalid) return;
        setSaving(true);
        // The API normalizes what is left.
        const entered = { contactEmail: contactEmail.trim() || null, contactPhone: contactPhone.trim() || null };
        try {
            await onSubmit(
                Object.fromEntries(
                    fields.map((field) => [
                        field,
                        entered[field],
                    ]),
                ),
            );
        } catch {
            // Already said so; stay open with what was entered.
            setSaving(false);
        }
    };

    return (
        <div className="flex flex-col gap-6">
            {fields.includes("contactEmail") ? (
                <Field data-invalid={showEmailError}>
                    <FieldLabel htmlFor={`${ids}-email`}>{t("admin.alumni.profile.contactDetails.email")}</FieldLabel>
                    <Input
                        id={`${ids}-email`}
                        type="email"
                        autoComplete="off"
                        value={contactEmail}
                        onChange={(event) => setContactEmail(event.target.value)}
                        aria-invalid={showEmailError}
                        aria-describedby={showEmailError ? `${ids}-email-error` : undefined}
                    />
                    {showEmailError ? (
                        <FieldError id={`${ids}-email-error`}>
                            {t("admin.alumni.profile.contactDetails.invalidEmail")}
                        </FieldError>
                    ) : null}
                </Field>
            ) : null}

            {fields.includes("contactPhone") ? (
                <Field data-invalid={showPhoneError}>
                    <FieldLabel htmlFor={`${ids}-phone`}>{t("admin.alumni.profile.contactDetails.phone")}</FieldLabel>
                    <Input
                        id={`${ids}-phone`}
                        type="tel"
                        inputMode="tel"
                        autoComplete="off"
                        value={contactPhone}
                        onChange={(event) => setContactPhone(typedPhone(event.target.value))}
                        aria-invalid={showPhoneError}
                        aria-describedby={showPhoneError ? `${ids}-phone-error` : undefined}
                    />
                    {showPhoneError ? (
                        <FieldError id={`${ids}-phone-error`}>
                            {t("admin.alumni.profile.contactDetails.invalidPhone")}
                        </FieldError>
                    ) : null}
                </Field>
            ) : null}

            <DialogFooter>
                <Button variant="ghost" onClick={onCancel}>
                    {t("common.actions.cancel")}
                </Button>
                <Button variant="secondary" disabled={saving} onClick={() => void submit()}>
                    {t("common.actions.save")}
                </Button>
            </DialogFooter>
        </div>
    );
}
